import { useEffect, useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import { format, formatDistanceToNow } from "date-fns";
import { Temporal } from "temporal-polyfill";
import {
  ArrowUpRight,
  Building2,
  Check,
  Copy,
  DollarSign,
  Edit,
  ExternalLink,
  MapPin,
  MoreHorizontal,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrgPath } from "@/hooks/useOrgPath";
import type { RouterOutputs } from "@/trpc";
import { getRecruiterTrackingUrl } from "@/features/recruiter-tracking/getRecruiterTrackingUrl";
import { formatFacilityWithType } from "@/features/job-post/utils/format-facility-with-type";
import { formatLocation } from "@/features/job-post/utils/format-location";
import { formatSalary } from "@/features/job-post/utils/format-salary";
import { JobPostProviderSpecialties } from "@/features/job-post/components/job-post-provider-specialties";
import { JobPostBadges } from "@/features/job-post/components/job-post-badges";

type OrganizationJobPost = RouterOutputs["jobPost"]["orgList"]["jobs"][number];

const RECENT_INTEREST_WINDOW_HOURS = 24;
const COPIED_FEEDBACK_DURATION_MS = 2000;
const COPY_TOAST_DURATION_MS = 3000;

function getJobPostInterestActivity({
  candidates,
  now = Temporal.Now.instant(),
}: {
  candidates: OrganizationJobPost["interestedCandidates"];
  now?: Temporal.Instant;
}) {
  const recentCutoff = now.subtract({ hours: RECENT_INTEREST_WINDOW_HOURS });
  let latestInterest: Temporal.Instant | null = null;
  let recentCount = 0;

  // Activity belongs to every interested provider, including providers whose
  // identities are hidden or have not been filled in yet.
  for (const candidate of candidates ?? []) {
    if (!candidate.interestedAt) {
      continue;
    }
    const interestedAt = Temporal.Instant.from(candidate.interestedAt);
    if (
      !latestInterest ||
      Temporal.Instant.compare(interestedAt, latestInterest) > 0
    ) {
      latestInterest = interestedAt;
    }
    if (
      Temporal.Instant.compare(interestedAt, recentCutoff) >= 0 &&
      Temporal.Instant.compare(interestedAt, now) <= 0
    ) {
      recentCount += 1;
    }
  }

  return { totalCount: candidates?.length ?? 0, recentCount, latestInterest };
}

export function OrganizationJobPostListItem({
  job,
  userId,
  canManageJobPosts,
}: {
  job: OrganizationJobPost;
  userId?: string;
  canManageJobPosts: boolean;
}) {
  const orgPath = useOrgPath();
  const titleId = useId();
  const [copied, setCopied] = useState(false);
  const title = job.title || "Untitled role";
  const location = formatLocation(job);
  const facility = formatFacilityWithType({
    facility: job.facility,
    facilityType: job.facilityType,
  });
  const showRemoteFriendly =
    job.remote && !location.toLowerCase().includes("remote");
  const activity = getJobPostInterestActivity({
    candidates: job.interestedCandidates,
  });
  const hasSecondaryActions = Boolean(
    userId || canManageJobPosts || job.externalUrl,
  );
  const dateSource = job.publishedAt || job.createdAt;
  let dateLabel = "Created";
  if (job.publishedAt) {
    dateLabel = job.reviewedAt ? "Published" : "Submitted";
  }

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeout = setTimeout(
      () => setCopied(false),
      COPIED_FEEDBACK_DURATION_MS,
    );
    return () => clearTimeout(timeout);
  }, [copied]);

  async function handleCopyLink() {
    if (!userId) {
      return;
    }
    const url = getRecruiterTrackingUrl(job.id, userId);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast("Link copied", {
        description: "Tracking link copied to clipboard.",
        duration: COPY_TOAST_DURATION_MS,
      });
    } catch {
      toast.error("Failed to copy link");
    }
  }

  return (
    <article
      aria-labelledby={titleId}
      className="grid min-w-0 gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-xs sm:p-5 lg:grid-cols-[minmax(0,1fr)_15rem] lg:gap-6"
    >
      <div className="min-w-0 space-y-3">
        <JobPostBadges job={job} />
        <h2
          id={titleId}
          className="text-lg font-semibold leading-7 tracking-tight text-slate-900 sm:text-xl"
        >
          <Link
            to={orgPath("/jobs/post/dashboard/" + job.id)}
            className="rounded-md [overflow-wrap:anywhere] hover:text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
          >
            {title}
          </Link>
        </h2>
        <div className="flex flex-col gap-x-5 gap-y-1.5 text-sm leading-6 text-slate-600 sm:flex-row sm:flex-wrap">
          {facility && (
            <span className="flex min-w-0 items-start gap-2">
              <Building2
                aria-hidden="true"
                className="mt-1 h-4 w-4 shrink-0 text-slate-500"
              />
              <span className="[overflow-wrap:anywhere]">{facility}</span>
            </span>
          )}
          <span className="flex min-w-0 items-start gap-2">
            <MapPin
              aria-hidden="true"
              className="mt-1 h-4 w-4 shrink-0 text-slate-500"
            />
            <span className="[overflow-wrap:anywhere]">
              {location || "Location not specified"}
              {showRemoteFriendly && (
                <span className="text-slate-500"> · Remote friendly</span>
              )}
            </span>
          </span>
        </div>
        <JobPostProviderSpecialties
          providerTypes={job.healthcareProfessionalTypes}
          specialties={job.specialties}
        />
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 border-t border-slate-100 pt-3">
          <p className="flex min-w-0 items-start gap-2 text-sm font-medium leading-6 text-slate-900">
            <DollarSign
              aria-hidden="true"
              className="mt-1 h-4 w-4 shrink-0 text-primary"
            />
            <span className="[overflow-wrap:anywhere]">
              <span className="sr-only">Compensation: </span>
              {job.wageFrequencyId
                ? formatSalary(job)
                : "Compensation not provided"}
            </span>
          </p>
          {dateSource && (
            <p className="text-xs leading-5 text-slate-500">
              {dateLabel}{" "}
              <time dateTime={dateSource}>
                {format(new Date(dateSource), "MMM d, yyyy")}
              </time>
            </p>
          )}
        </div>
      </div>
      <div className="flex min-w-0 flex-col justify-between gap-4 border-t border-slate-200 pt-4 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-slate-900">
            <Users
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-primary"
            />
            <p className="text-sm">
              <span className="text-xl font-semibold tabular-nums">
                {activity.totalCount}
              </span>{" "}
              interested {activity.totalCount === 1 ? "provider" : "providers"}
            </p>
          </div>
          {activity.recentCount > 0 && (
            <p className="text-sm font-medium text-primary">
              {activity.recentCount} new in the last 24 hours
            </p>
          )}
          {activity.latestInterest && (
            <p className="text-xs leading-5 text-slate-500">
              Latest interest{" "}
              <time dateTime={activity.latestInterest.toString()}>
                {formatDistanceToNow(
                  new Date(activity.latestInterest.epochMilliseconds),
                  { addSuffix: true },
                )}
              </time>
            </p>
          )}
          {activity.totalCount === 0 && (
            <p className="text-xs leading-5 text-slate-500">No interest yet</p>
          )}
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <Button
            asChild
            variant="outline"
            className="min-w-0 flex-1 gap-2 px-3 text-primary"
          >
            <Link to={orgPath("/jobs/post/dashboard/" + job.id)}>
              View dashboard
              <ArrowUpRight aria-hidden="true" className="h-4 w-4 shrink-0" />
            </Link>
          </Button>
          {hasSecondaryActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 w-12 shrink-0 px-0"
                  aria-label={`Actions for ${title}`}
                >
                  <MoreHorizontal aria-hidden="true" className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                collisionPadding={16}
                className="max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] w-56 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border-slate-200 bg-white p-1.5"
              >
                {canManageJobPosts && (
                  <DropdownMenuItem
                    asChild
                    className="min-h-12 gap-2 rounded-lg px-3 py-2 text-slate-700 focus:bg-primary/5 focus:text-primary"
                  >
                    <Link to={orgPath("/jobs/post/edit/" + job.id)}>
                      <Edit
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0 text-slate-500"
                      />
                      Edit job post
                    </Link>
                  </DropdownMenuItem>
                )}
                {userId && (
                  <DropdownMenuItem
                    onSelect={() => {
                      void handleCopyLink();
                    }}
                    className="min-h-12 gap-2 rounded-lg px-3 py-2 text-slate-700 focus:bg-primary/5 focus:text-primary"
                  >
                    {copied ? (
                      <Check
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0 text-green-600"
                      />
                    ) : (
                      <Copy
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0 text-slate-500"
                      />
                    )}
                    {copied ? "Copied" : "Copy Link"}
                  </DropdownMenuItem>
                )}
                {job.externalUrl && (
                  <DropdownMenuItem
                    asChild
                    className="min-h-12 gap-2 rounded-lg px-3 py-2 text-slate-700 focus:bg-primary/5 focus:text-primary"
                  >
                    <a
                      href={job.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0 text-slate-500"
                      />
                      External job
                    </a>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </article>
  );
}
